/** Lo mínimo para dibujar el árbol: familias y, de cada integrante, de qué familia viene. */
export interface TreeFamily {
  id: string;
  name: string;
  members: { personId: string; name: string; originFamilyId: string | null }[];
}

/**
 * Árbol familiar por generaciones: arriba las familias de origen (papás, abuelos), en medio la
 * familia y las de sus hermanos, abajo las que formaron sus hijos y nietos.
 */
export function FamilyTreeView({ families, rootId }: { families: TreeFamily[]; rootId: string }) {
  const byId = new Map(families.map((f) => [f.id, f]));
  const parentsOf = (ids: string[]) => [...new Set(ids.flatMap((id) => byId.get(id)?.members.map((m) => m.originFamilyId) ?? []))].filter((id): id is string => Boolean(id && byId.has(id)));
  const childrenOf = (ids: string[]) => families.filter((g) => g.members.some((m) => m.originFamilyId && ids.includes(m.originFamilyId))).map((g) => g.id);

  const seen = new Set<string>([rootId]);
  const fresh = (ids: string[]) => ids.filter((id) => !seen.has(id) && seen.add(id));
  const up: string[][] = [];
  for (let level = [rootId]; up.length < 4; ) {
    level = fresh(parentsOf(level));
    if (!level.length) break;
    up.unshift(level);
  }
  // Las familias de los hermanos: vienen de la misma familia de origen.
  const middle = [rootId, ...fresh(childrenOf(up.at(-1) ?? []))];
  const down: string[][] = [];
  for (let level = middle; down.length < 4; ) {
    level = fresh(childrenOf(level));
    if (!level.length) break;
    down.push(level);
  }

  const box = (id: string) => {
    const f = byId.get(id)!;
    return (
      <div key={id} className={`family-tree__family${id === rootId ? ' family-tree__family--root' : ''}`}>
        <strong>{f.name}</strong>
        <ul>
          {f.members.map((m) => (
            <li key={m.personId}>
              {m.name}
              {m.originFamilyId && byId.has(m.originFamilyId) && m.originFamilyId !== id ? <span className="muted small"> · de {byId.get(m.originFamilyId)!.name}</span> : null}
            </li>
          ))}
        </ul>
      </div>
    );
  };
  const row = (ids: string[], i: number) => (
    <div key={i} className="family-tree__row">
      {ids.map(box)}
    </div>
  );

  return (
    <div className="family-tree">
      {up.map(row)}
      {row(middle, 100)}
      {down.map((ids, i) => row(ids, 200 + i))}
      {up.length === 0 && down.length === 0 && middle.length === 1 ? (
        <p className="muted small">Aún no hay familias relacionadas. La administración indica de qué familia viene cada quien en Miembros → Familias → «Árbol familiar».</p>
      ) : null}
    </div>
  );
}
